import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { useSensorKitBySerialNumber } from "@/sensorKits/api";
import { AlertTriangle, LineChart, Wrench } from "lucide-react";
import { InstrumentedErrorBoundary } from "@/components/error-boundary";
import { getShortEquipmentDetails } from "@/installation/installation";
import { SyncFeedbackForm } from "./SyncFeedbackForm";

const SensorKitCardContainer = ({
                                  children,
                                  className = ""
                                }: {
  children: React.ReactNode,
  className?: string
}) => (
  <div className={`mx-auto ${className}`}>
    <Card className="flex flex-col gap-4">{children}</Card>
  </div>
);

const SensorKitNotFoundError = ({
                                  resetErrorBoundary,
                                  serialNumber
                                }: {
  resetErrorBoundary: () => void;
  serialNumber?: string;
}) => (
  <SensorKitCardContainer className="max-w-md">
    <div className="p-6 text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertTriangle className="h-6 w-6 text-red-600"/>
      </div>
      <h1 className="text-xl font-bold mb-2">Serial number not found</h1>
      <p className="text-gray-500 mb-4">
        We couldn't find a unit with serial number:
        <span className="font-medium block mt-1">{serialNumber}</span>
      </p>
      <Button onClick={resetErrorBoundary}>
        Try another serial number
      </Button>
    </div>
  </SensorKitCardContainer>
);


const SensorKitTimelineView = ({ sensorKitSerialNumber }: { sensorKitSerialNumber: string }) => {
  const { data: sensorKit } = useSensorKitBySerialNumber(sensorKitSerialNumber);
  const navigate = useNavigate();

  const installations = sensorKit?.current_installations ?? [];
  const hasInstallations = installations.length > 0;
  const showInstallButton = !hasInstallations || sensorKit?.allow_multiple_installations;

  return (
    <SensorKitCardContainer className="max-w-2xl">
      <div className="p-12 flex flex-col gap-6">
        <h1 className="text-2xl font-bold">Thalo Copilot monitoring</h1>
        <div>
          This Sensor Kit is made by Thalo Labs, for real-time monitoring and preventative maintenance of HVAC equipment.
        </div>
        <div className="rounded-lg p-4 bg-gray-50 flex items-center flex-col gap-2">
          <span
            className="mx-auto text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent from-thalo-blue via-thalo-blueLight to-thalo-blue animate-text">
            {sensorKitSerialNumber}
          </span>
        </div>

        {hasInstallations && (
          <div className="flex flex-col gap-3">
            {installations.map((installation) => (
              <div
                key={installation.id}
                className="grid grid-cols-[1fr_auto] items-center gap-4 p-3 rounded-lg border bg-white"
              >
                <div className="flex flex-col min-w-0">
                  <span className="font-medium">{installation.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {getShortEquipmentDetails(installation.asset)}
                  </span>
                </div>
                <Button
                  variant="thalo"
                  size="sm"
                  onClick={() => navigate({
                    to: "/organization/$organizationId/locations/$locationId/equipment/$equipmentId",
                    params: {
                      organizationId: installation.location.organization_id,
                      locationId: installation.location.id,
                      equipmentId: installation.equipment
                    }
                  })}
                  className="flex flex-row gap-2 items-center whitespace-nowrap"
                >
                  <LineChart size={16}/>
                  View data
                </Button>
              </div>
            ))}
          </div>
        )}

        {showInstallButton && (
          <div className="flex flex-col gap-4">
            {!hasInstallations && <p>Ready to start monitoring?</p>}
            <Button
              variant="thalo"
              onClick={() => navigate({
                to: '/installFlow/select-organization',
              })}
              className="flex flex-row gap-2 items-center"
            >
              <Wrench size={16}/>
              Install on your equipment
            </Button>
          </div>
        )}
      </div>
    </SensorKitCardContainer>
  );
};

export const SensorKitSyncPage = ({ sensorKitSerialNumber }: { sensorKitSerialNumber?: string }) => {
  const [serialNumber, setSerialNumber] = useState("");
  const navigate = useNavigate();

  const handleSerialNumberSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (serialNumber.trim()) {
      navigate({ to: `/sync/${serialNumber}` });
    }
  };

  const handleErrorReset = () => {
    navigate({ to: '/sync' });
  };

  if (!sensorKitSerialNumber) {
    return (
      <div className="p-4">
        <SensorKitCardContainer className="max-w-md">
          <div className="p-6">
            <h1 className="text-xl font-bold mb-4">Enter Sensor Kit Serial Number</h1>
            <form onSubmit={handleSerialNumberSubmit}>
              <div className="space-y-4">
                <Input
                  placeholder="Serial Number"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!serialNumber.trim()}
                >
                  Continue to Sync
                </Button>
              </div>
            </form>
          </div>
        </SensorKitCardContainer>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <InstrumentedErrorBoundary
        fallback={({ error }) => (
          <SensorKitNotFoundError
            error={error as Error}
            resetErrorBoundary={handleErrorReset}
            serialNumber={sensorKitSerialNumber}
          />
        )}
      >
        <SensorKitTimelineView sensorKitSerialNumber={sensorKitSerialNumber}/>
      </InstrumentedErrorBoundary>

      <div className="max-w-2xl mx-auto">
        <SyncFeedbackForm sensorKitSerialNumber={sensorKitSerialNumber} />
      </div>
    </div>
  );
};